import { useRef, useState } from 'react';
import type { DiagnosticAnswers, DiagnosticIdentityProvider, DiagnosticView } from '@shared/prospect-diagnostic';
import { savePendingProvider, sendLeadCode, startLeadOAuth, verifyLeadCode } from '@/lib/lead-diagnostic';
import { type TurnstileAction, getTurnstileToken } from '@/lib/turnstile';
import { DiagnosticAlert, DiagnosticInput, QuestionTitle, diagnosticPrimary } from '@/components/diagnostic/DiagnosticParts';

const PROVIDER_LABELS: Record<DiagnosticIdentityProvider, string> = {
  google: 'Continuer avec Google',
  microsoft: 'Continuer avec Microsoft',
};

const providerButton =
  'flex min-h-[56px] w-full items-center justify-center gap-3 rounded-full border-2 border-domino-sand bg-white px-6 text-base font-semibold text-domino-ink transition-colors hover:border-domino-aubergine/40 disabled:opacity-50';

interface Props {
  answers: DiagnosticAnswers;
  providers: DiagnosticIdentityProvider[];
  onBack: () => void;
  onOpened: (token: string, diagnostic: DiagnosticView) => void;
}

/** Last screen before any AI call: prove a work mailbox (provider sign-in or a code). */
export const DiagnosticIdentity = ({ answers, providers, onBack, onOpened }: Props) => {
  const turnstileRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const withTurnstile = async (action: TurnstileAction): Promise<string | null> => {
    if (!turnstileRef.current) return null;
    try {
      return await getTurnstileToken(turnstileRef.current, action);
    } catch (turnstileError) {
      console.error('[diagnostic] turnstile', turnstileError);
      setError('La vérification n’a pas abouti. Rechargez la page.');
      return null;
    }
  };

  const signInWith = async (provider: DiagnosticIdentityProvider) => {
    setError(null);
    setBusy(true);
    const token = await withTurnstile('diagnostic_signin');
    if (!token) { setBusy(false); return; }
    const result = await startLeadOAuth(provider, token);
    if (!result.ok) { setBusy(false); setError(result.message); return; }
    savePendingProvider(provider);
    window.location.assign(result.url);
  };

  const askCode = async () => {
    setError(null);
    setBusy(true);
    const token = await withTurnstile('diagnostic_code');
    if (!token) { setBusy(false); return; }
    const result = await sendLeadCode(email.trim(), token);
    setBusy(false);
    if (!result.ok) { setError(result.message); return; }
    setCodeSentTo(email.trim());
    setCode('');
  };

  const openWithCode = async () => {
    if (!codeSentTo) return;
    setError(null);
    setBusy(true);
    const result = await verifyLeadCode(codeSentTo, code.trim(), answers);
    setBusy(false);
    if (!result.ok) { setError(result.message); return; }
    onOpened(result.token, result.diagnostic);
  };

  return (
    <>
      <QuestionTitle>Où vous envoyer le résultat ?</QuestionTitle>
      {error && <DiagnosticAlert>{error}</DiagnosticAlert>}

      {providers.length > 0 && !codeSentTo && (
        <div className="flex flex-col gap-3">
          {providers.map((provider) => (
            <button key={provider} type="button" disabled={busy} onClick={() => void signInWith(provider)} className={providerButton}>
              {PROVIDER_LABELS[provider]}
            </button>
          ))}
          <p className="text-center text-sm font-semibold text-domino-inkSoft">ou</p>
        </div>
      )}

      {codeSentTo ? (
        <div className="flex flex-col gap-4">
          <DiagnosticInput id="diag-code" label={`Code reçu à ${codeSentTo}`} value={code} onChange={setCode} inputMode="numeric" autoComplete="one-time-code" />
          <button type="button" disabled={busy || !/^\d{6}$/.test(code.trim())} onClick={() => void openWithCode()} className={diagnosticPrimary}>
            Ouvrir mon diagnostic
          </button>
          <button type="button" disabled={busy} onClick={() => { setCodeSentTo(null); setError(null); }} className="self-start text-sm font-semibold text-domino-inkSoft hover:text-domino-aubergine">
            Changer d’adresse
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <DiagnosticInput id="diag-email" label="E-mail professionnel" type="email" value={email} onChange={setEmail} inputMode="email" autoComplete="email" />
          <button type="button" disabled={busy || !email.includes('@')} onClick={() => void askCode()} className={diagnosticPrimary}>
            Recevoir un code
          </button>
        </div>
      )}

      <div ref={turnstileRef} />
      <button type="button" disabled={busy} onClick={onBack} className="min-h-[44px] self-start px-1 text-base font-semibold text-domino-inkSoft hover:text-domino-aubergine">
        ← Retour
      </button>
    </>
  );
};
